import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyCollaborationTimelineApi } from '../../api/journeyCollaborationTimelineApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { useJourneyNoticeUnreadCount } from '../journey-notice/useJourneyNoticeUnreadCount';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { memberDisplayName } from '../member-display/memberDisplayModel';
import {
  appendCollaborationTimelineItems,
  classifyCollaborationTimelineError,
  collaborationTimelineErrorMessage,
  normalizeCollaborationTimelinePage,
} from './collaborationTimelineModel';
import '../journey-workspace/journey-workspace.css';
import './journey-collaboration-timeline.css';

const PAGE_LIMIT = 20;

function TimelineItem({ item, actorLabel }) {
  return (
    <li>
      <article
        className="journey-collaboration-timeline__item"
        aria-labelledby={`timeline-title-${item.timelineItemId}`}
      >
        <p className="journey-collaboration-timeline__meta">
          <span className="journey-collaboration-timeline__type">{item.eventLabel}</span>
          {item.sourceLabel ? ` · ${item.sourceLabel}` : ''}
          {item.status ? ` · ${item.status}` : ''}
          {item.severity ? ` · ${item.severity}` : ''}
        </p>
        <h3 className="journey-collaboration-timeline__title" id={`timeline-title-${item.timelineItemId}`}>
          {item.title}
        </h3>
        {item.summary ? <p className="journey-collaboration-timeline__summary">{item.summary}</p> : null}
        <p className="journey-collaboration-timeline__source">
          {item.occurredAt ? (
            <time dateTime={item.occurredAt}>{formatDateTime(item.occurredAt)}</time>
          ) : null}
          {actorLabel ? ` · ${actorLabel}` : ''}
        </p>
        {item.sourceRoute ? (
          <p className="journey-collaboration-timeline__actions">
            <Link to={item.sourceRoute}>{item.ctaLabel}</Link>
          </p>
        ) : null}
      </article>
    </li>
  );
}

export default function JourneyCollaborationTimelinePage() {
  const { journeyId } = useParams();
  const { t } = useTravelTranslation();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const { unreadCount } = useJourneyNoticeUnreadCount(journeyId);

  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [journey, setJourney] = useState(null);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const actorIds = useMemo(
    () => items.map((item) => item.actorMemberId).filter(Boolean),
    [items],
  );
  const actorSummaries = useMemberDisplayNames(actorIds);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setError(null);
    setActionError('');
    try {
      const [detailPayload, listPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyCollaborationTimelineApi.list(journeyId, { limit: PAGE_LIMIT }),
      ]);
      if (requestId !== generation.current) return;
      const page = normalizeCollaborationTimelinePage(listPayload);
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyCollaborationTimelineError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || kind === 'unauthorized' || access === 'forbidden') setPhase('forbidden');
      else {
        // initial generic error：只走 canonical mapper，不經 raw-capable ErrorMessage helper。
        setError(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  async function loadMore() {
    if (loadingMore || !hasMore || !nextCursor) return;
    setLoadingMore(true);
    setActionError('');
    try {
      const payload = await journeyCollaborationTimelineApi.list(journeyId, {
        limit: PAGE_LIMIT,
        cursorSortTime: nextCursor.sortTime,
        cursorTimelineItemId: nextCursor.timelineItemId,
      });
      const page = normalizeCollaborationTimelinePage(payload);
      setItems((current) => appendCollaborationTimelineItems(current, page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (cause) {
      setActionError(collaborationTimelineErrorMessage(cause, '無法載入更多動態。'));
    } finally {
      setLoadingMore(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state" data-testid="timeline-loading">
        <LoadingState label={t('travel.common.loading')} />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state" data-testid="timeline-not-found">
        <h1>找不到這趟旅程</h1>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state" data-testid="timeline-forbidden">
        <h1>沒有權限查看旅程動態</h1>
        <p>只有進行中的旅程成員可以讀取協作動態。前端隱藏不是權限依據。</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = error ? resolveUiErrorCopy(error, t) : null;
    return (
      <main className="journey-workspace-state" data-testid="timeline-error">
        <ErrorState
          title={errorCopy?.title || t('travel.common.apiError.unknown.title')}
          description={errorCopy?.description || t('travel.common.apiError.unknown.description')}
          retryable={Boolean(errorCopy?.retryable)}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  return (
    <main className="journey-workspace journey-collaboration-timeline">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">旅程動態</p>
          <h1>旅程動態</h1>
          <p className="journey-workspace__meta">
            {journey?.title || '未命名行程'}
            {journey?.lifecycleProductLabel || journey?.lifecycleLabel
              ? ` · ${journey.lifecycleProductLabel || journey.lifecycleLabel}`
              : ''}
          </p>
        </div>
        <div className="journey-collaboration-timeline__header-actions">
          <button type="button" onClick={() => load()}>重新整理</button>
          <Link className="journey-workspace__back" to={`/journeys/${journey.journeyId}`}>{WORKSPACE_PRODUCT_CHROME.overview}</Link>
        </div>
      </header>

      <JourneyWorkspaceNav journeyId={journey.journeyId} unreadCount={unreadCount} />

      <div aria-live="polite">
        {actionError ? <p role="alert">{actionError}</p> : null}
      </div>

      {items.length === 0 ? (
        <EmptyState
          className="journey-workspace__hint"
          data-testid="timeline-empty"
          title="目前沒有旅程動態"
        />
      ) : (
        <ul className="journey-collaboration-timeline__list">
          {items.map((item) => (
            <TimelineItem
              key={item.timelineItemId}
              item={item}
              actorLabel={item.actorMemberId
                ? memberDisplayName(item.actorMemberId, actorSummaries)
                : ''}
            />
          ))}
        </ul>
      )}

      {hasMore ? (
        <p>
          <button
            type="button"
            className="journey-collaboration-timeline__load-more"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? '載入中…' : '載入更多動態'}
          </button>
        </p>
      ) : null}
    </main>
  );
}
